import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createLoanCrudHooks } from './lib/create-crud-hooks';
import { api } from '@/lib/api';

export interface Guarantor {
  id: string;
  loanId: string;
  name: string;
  entityType: 'INDIVIDUAL' | 'COMPANY' | 'TRUST';
  abn?: string;
  email?: string;
  phone?: string;
  guaranteeType: 'PERSONAL' | 'CORPORATE' | 'DIRECTOR' | 'THIRD_PARTY' | 'LIMITED_RECOURSE';
  limitOfLiability: string; // decimal as string
  guaranteeDate?: string; // ISO date
  expiryDate?: string; // ISO date
  status: 'ACTIVE' | 'RELEASED' | 'EXPIRED';
  notes?: string;
  createdAt: string;
  releasedAt?: string;
  releasedReason?: string;
}

export interface GuarantorSummary {
  totalGuarantors: number;
  totalLiability: string;
  activeCount: number;
  releasedCount: number;
  expiredCount: number;
}

interface GuarantorSummaryApiResponse {
  totalGuarantors: number;
  activeGuarantors: number;
  releasedGuarantors: number;
  expiredGuarantees: number;
  totalLimitOfLiability: number | string;
}

// ── Factory CRUD hooks ───────────────────────────────────

const { useList } = createLoanCrudHooks<Guarantor, any, any>('guarantors', 'guarantors');

export function useGuarantors(loanId: string) {
  return useList(loanId);
}

// ── Create guarantor (manual - loanId extracted from payload) ──

export function useCreateGuarantor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      loanId,
      ...payload
    }: Omit<Guarantor, 'id' | 'createdAt' | 'status'> & { loanId: string }) => {
      const { data } = await api.post<Guarantor>(`/loans/${loanId}/guarantors`, payload);
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['guarantors', vars.loanId] });
      qc.invalidateQueries({ queryKey: ['guarantor-summary', vars.loanId] });
    },
  });
}

// ── Update guarantor (manual - loanId extracted from payload) ──

export function useUpdateGuarantor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      loanId,
      ...payload
    }: Partial<Guarantor> & { id: string; loanId: string }) => {
      const { data } = await api.patch<Guarantor>(`/loans/${loanId}/guarantors/${id}`, payload);
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['guarantors', vars.loanId] });
      qc.invalidateQueries({ queryKey: ['guarantor-summary', vars.loanId] });
    },
  });
}

// ── Release guarantor (manual - custom action) ────────────

export function useReleaseGuarantor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, loanId, reason }: { id: string; loanId: string; reason?: string }) => {
      const { data } = await api.patch<Guarantor>(`/loans/${loanId}/guarantors/${id}/release`, {
        reason,
      });
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['guarantors', vars.loanId] });
      qc.invalidateQueries({ queryKey: ['guarantor-summary', vars.loanId] });
    },
  });
}

// ── Guarantor summary (manual - summary query) ──────────

export function useGuarantorSummary(loanId: string) {
  return useQuery<GuarantorSummary>({
    queryKey: ['guarantor-summary', loanId],
    queryFn: async () => {
      const { data } = await api.get<GuarantorSummaryApiResponse>(
        `/loans/${loanId}/guarantors/summary`,
      );
      return {
        totalGuarantors: data.totalGuarantors ?? 0,
        totalLiability: String(data.totalLimitOfLiability ?? 0),
        activeCount: data.activeGuarantors ?? 0,
        releasedCount: data.releasedGuarantors ?? 0,
        expiredCount: data.expiredGuarantees ?? 0,
      };
    },
    enabled: !!loanId,
  });
}
